import { useMemo } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

import { groupByRank } from "@/features/match/matchFlow.machine";
import type { useMatchFlow } from "@/features/match/useMatchFlow";
import { useTranslations } from "@/i18n";
import { usePlayers } from "@/store/selectors";
import { isTournament } from "@/types";
import { TEAM_COLORS, type TeamColorKey, theme } from "@/ui/theme";

// ── Helpers ──────────────────────────────────────────────────

const TEAM_COLOR_ORDER: TeamColorKey[] = ["team-a", "team-b", "team-c", "team-d"];
const MEDALS: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

// ── Styled ───────────────────────────────────────────────────

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StageTag = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${theme.colors.primary};
`;

const Title = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: 2.25rem;
  line-height: 0.95;
`;

const Meta = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
`;

const HeroPodium = styled(motion.div)`
  border-radius: ${theme.radii["3xl"]};
  border: 1px solid ${theme.colors.border};
  background: ${theme.gradients.court};
  padding: 1.25rem;
  box-shadow: ${theme.shadow.card};
  text-align: center;

  background-image:
    linear-gradient(${theme.colors.courtLine} 1px, transparent 1px),
    linear-gradient(90deg, ${theme.colors.courtLine} 1px, transparent 1px);
  background-size: 32px 32px;
`;

const TrophyEmoji = styled.div`
  font-size: 3.75rem;
`;

const WinnerRow = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
`;

const WinnerPill = styled.span<{ teamColor: string }>`
  border-radius: ${theme.radii.full};
  padding: 0.375rem 1rem;
  font-family: ${theme.fonts.display};
  font-size: 1.25rem;
  border: 1px solid color-mix(in oklab, ${(p) => p.teamColor} 40%, transparent);
  background: color-mix(in oklab, ${(p) => p.teamColor} 15%, transparent);
  color: ${(p) => p.teamColor};
`;

const ChampLabel = styled.p`
  margin-top: 0.75rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${theme.colors.textMuted};
`;

const StandingsList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StandingItem = styled(motion.li)<{ teamColor: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent);
  padding: 0.75rem;
  outline: 1px solid color-mix(in oklab, ${(p) => p.teamColor} 30%, transparent);
  margin: 0 1px;
`;

const MedalBox = styled.span`
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: ${theme.radii.xl};
  background: color-mix(in oklab, ${theme.colors.bg} 60%, transparent);
  font-size: 1.25rem;
  border: 1px solid ${theme.colors.border};
`;

const StandingBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const StandingName = styled.div`
  font-family: ${theme.fonts.display};
  font-size: 1.125rem;
`;

const StandingPlayers = styled.div`
  font-size: 0.75rem;
  color: color-mix(in oklab, ${theme.colors.text} 70%, transparent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RankBadgeEl = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${theme.colors.textMuted};
`;

// ── Component ──────────────────────────────────────────────────

type FlowApi = ReturnType<typeof useMatchFlow>;

export const RecapStage = ({ flow }: { flow: FlowApi }) => {
  const { t } = useTranslations();
  const playersById = usePlayers();

  const { activity, teams, rankEntries, tournamentResults } = flow;

  if (!activity) return null;

  // Build ranked entries — either from rankEntries (FFA/Teams) or tournamentResults
  const isTourney = isTournament(activity);

  const standings = useMemo(() => {
    if (isTourney && tournamentResults.length > 0) {
      // Tournament: results are tiers of player IDs
      return tournamentResults.flatMap((tier, tierIdx) =>
        tier.map((pid) => ({
          rank: tierIdx + 1,
          label: playersById[pid]?.name ?? "?",
          names: "",
          colorKey: TEAM_COLOR_ORDER[tierIdx % TEAM_COLOR_ORDER.length]!,
          playerIds: [pid],
        })),
      );
    }

    // FFA/Teams: use rankEntries
    const grouped = groupByRank(rankEntries);
    return grouped.flatMap(([rank, teamIdxs]) =>
      teamIdxs.map((ti) => {
        const team = teams[ti]!;
        const isSolo = team.length === 1;
        return {
          rank,
          label: isSolo ? (playersById[team[0]!]?.name ?? "?") : t("general.teamN", ti + 1),
          names: isSolo ? "" : team.map((pid) => playersById[pid]?.name ?? "?").join(", "),
          colorKey: TEAM_COLOR_ORDER[ti % TEAM_COLOR_ORDER.length]!,
          playerIds: team,
        };
      }),
    );
  }, [isTourney, tournamentResults, rankEntries, teams, playersById]);

  const winners = standings.filter((s) => s.rank === 1);

  return (
    <Content>
      <motion.header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <StageTag>
          <Sparkles size={12} /> {t("arena.recap")}
        </StageTag>
        <Title>{winners.length > 1 ? t("arena.itsATie") : t("arena.weHaveAWinner")}</Title>
        <Meta>{activity.name}</Meta>
      </motion.header>

      <HeroPodium
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 22 }}
      >
        <TrophyEmoji>🏆</TrophyEmoji>
        <WinnerRow>
          {winners.map((w, i) => (
            <WinnerPill key={i} teamColor={TEAM_COLORS[w.colorKey]}>
              {w.label}
            </WinnerPill>
          ))}
        </WinnerRow>
        <ChampLabel>{winners.length > 1 ? t("arena.coChampions") : t("arena.champion")}</ChampLabel>
      </HeroPodium>

      <StandingsList>
        {standings.map((s, i) => {
          const color = TEAM_COLORS[s.colorKey];
          const medal = MEDALS[s.rank] ?? `#${s.rank}`;
          return (
            <StandingItem
              key={`${s.rank}-${i}`}
              teamColor={color}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <MedalBox>{medal}</MedalBox>
              <StandingBody>
                <StandingName>{s.label}</StandingName>
                {s.names && <StandingPlayers>{s.names}</StandingPlayers>}
              </StandingBody>
              <RankBadgeEl>#{s.rank}</RankBadgeEl>
            </StandingItem>
          );
        })}
      </StandingsList>
    </Content>
  );
};
